import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, BookOpen, ChevronDown, FileText, Search, SlidersHorizontal, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useCourseModules } from "../../contexts/CourseContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { WritingHubSkeleton } from "../../components/skeletons/PageSkeletons";
import "./course.css";
import ImpactSlideshow from "../../components/course/ImpactSlideshow";
import { ImpactSlide, usableImpactSlides } from "../../types/impactSlides";
import { WRITING_LABEL_GROUPS, toggleWritingLabel } from "../../constants/writingLabels";
import { getLastModule } from "../../utils/courseLocalProgress";

type WritingItemType = "course" | "article";

interface WritingItem {
  type: WritingItemType;
  key: string;
  title: string;
  tagline: string;
  coverImageUrl?: string;
  coverImageIsPlaceholder?: boolean;
  featured?: boolean;
  impactSlides?: ImpactSlide[];
  impactAnimated?: boolean;
  order: number;
  statusText: string;
  ctaText: string;
  to: string;
  /** Where the card's button leads when it differs from the card itself */
  ctaTo?: string;
  labels: string[];
}

type TypeFilter = "all" | WritingItemType;

const TYPE_TABS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "course", label: "Courses" },
  { value: "article", label: "Articles" },
];

const STACKED_QUERY = "(max-width: 760px)";

const WritingPage: React.FC = () => {
  usePageTitle("Writing");
  const { courses, chapters, courseModules, articles, loading } = useCourseModules();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [labelFilters, setLabelFilters] = useState<string[]>([]);
  const [openGroups, setOpenGroups] = useState<string[]>(() => WRITING_LABEL_GROUPS.map((group) => group.name));
  const filtersRef = useRef<HTMLElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  // Below the stacked breakpoint the topic list lives in a slide-in drawer
  const [isStacked, setIsStacked] = useState(() => window.matchMedia(STACKED_QUERY).matches);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(STACKED_QUERY);
    const update = () => {
      setIsStacked(query.matches);
      if (!query.matches) setDrawerOpen(false);
    };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // While the drawer is open: lock the page behind it, focus it, and let Escape close it
  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    const opener = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    drawerCloseRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      opener?.focus();
    };
  }, [drawerOpen]);

  // The sidebar grows with its open groups but stops at the viewport bottom (then scrolls inside).
  // Its top moves until it sticks, so the cap is recalculated from its live position.
  useEffect(() => {
    const aside = filtersRef.current;
    if (!aside) return;
    const stacked = window.matchMedia(STACKED_QUERY);
    const desktop = window.matchMedia("(min-width: 1024px)");
    let frame = 0;
    const update = () => {
      frame = 0;
      if (stacked.matches) {
        aside.style.maxHeight = "";
        return;
      }
      // Below lg the floating nav sits at the bottom, so leave room for it.
      const bottomGap = desktop.matches ? 24 : 104;
      aside.style.maxHeight = `${Math.max(200, window.innerHeight - aside.getBoundingClientRect().top - bottomGap)}px`;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [loading]);

  const items: WritingItem[] = useMemo(() => {
    const courseItems: WritingItem[] = courses
      .filter((course) => course.status === "published")
      .map((course) => {
        const modules = courseModules.filter((m) => m.courseSlug === course.slug);
        const writtenCount = modules.filter((m) => m.published).length;
        const orderedChapters = chapters
          .filter((c) => c.courseSlug === course.slug && c.published)
          .sort((a, b) => a.order - b.order);
        const readableModules = orderedChapters
          .flatMap((ch) => modules.filter((m) => m.chapterSlug === ch.slug && m.published))
          .sort((a, b) => a.order - b.order);
        const lastSlug = getLastModule(course.slug);
        const resumeModule = readableModules.find((m) => m.slug === lastSlug);
        const ctaModule = resumeModule || readableModules[0];

        return {
          type: "course",
          key: course.slug,
          title: course.title,
          tagline: course.tagline,
          coverImageUrl: course.coverImageUrl,
          coverImageIsPlaceholder: course.coverImageIsPlaceholder,
          featured: course.featured,
          impactSlides: course.impactSlides,
          impactAnimated: course.impactAnimated,
          order: course.order,
          statusText:
            writtenCount > 0
              ? `${writtenCount} published ${writtenCount === 1 ? "chapter" : "chapters"}`
              : "In development — no chapters published yet",
          ctaText: resumeModule ? "Continue where you left off" : ctaModule ? "Start the series" : "View the roadmap",
          to: `/writing/series/${course.slug}`,
          ctaTo: ctaModule ? `/writing/series/${course.slug}/${ctaModule.slug}` : undefined,
          labels: course.labels || [],
        };
      });

    const articleItems: WritingItem[] = articles
      .filter((article) => article.published)
      .map((article) => ({
        type: "article",
        key: article.slug,
        title: article.title,
        tagline: article.tagline,
        coverImageUrl: article.coverImageUrl,
        coverImageIsPlaceholder: article.coverImageIsPlaceholder,
        featured: article.featured,
        impactSlides: article.impactSlides,
        impactAnimated: article.impactAnimated,
        order: article.order,
        statusText: "Standalone article",
        ctaText: "Read the article",
        to: `/writing/articles/${article.slug}`,
        labels: article.labels || [],
      }));

    return [...courseItems, ...articleItems].sort((a, b) =>
      !!a.featured !== !!b.featured
        ? a.featured
          ? -1
          : 1
        : a.type === b.type
          ? a.order - b.order
          : a.type === "course"
            ? -1
            : 1
    );
  }, [courses, chapters, courseModules, articles]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const searchMatched = items.filter(
    (item) =>
      !normalizedSearch ||
      item.title.toLowerCase().includes(normalizedSearch) ||
      item.tagline.toLowerCase().includes(normalizedSearch)
  );
  const typeMatched = searchMatched.filter((item) => typeFilter === "all" || item.type === typeFilter);
  const visibleItems = typeMatched.filter(
    (item) => labelFilters.length === 0 || labelFilters.some((label) => item.labels.includes(label))
  );

  const counts = {
    all: searchMatched.length,
    course: searchMatched.filter((item) => item.type === "course").length,
    article: searchMatched.filter((item) => item.type === "article").length,
  };

  const labelCounts = Object.fromEntries(
    WRITING_LABEL_GROUPS.flatMap((group) =>
      group.labels.map((label) => [label, typeMatched.filter((item) => item.labels.includes(label)).length])
    )
  );

  const renderCard = (item: WritingItem) => {
    const impactSlides = item.featured && item.impactAnimated ? usableImpactSlides(item.impactSlides) : [];
    // The cover image always closes the sequence as the "solution".
    const slides: ImpactSlide[] =
      impactSlides.length > 0 && item.coverImageUrl
        ? [...impactSlides, { id: "cover", type: "image", imageUrl: item.coverImageUrl }]
        : impactSlides;
    return (
      <article key={`${item.type}-${item.key}`} className="writing-hub__card writing-hub__card--link">
        <Link className="writing-hub__card-overlay" to={item.to} aria-label={item.title} />
        <div className="writing-hub__card-cover">
          {slides.length > 0 ? (
            <ImpactSlideshow slides={slides} />
          ) : (
            <>
              {item.coverImageUrl && <img src={item.coverImageUrl} alt="" />}
              {item.coverImageIsPlaceholder && (
                <span className="writing-hub__cover-placeholder">Placeholder cover</span>
              )}
            </>
          )}
        </div>
        <div className="writing-hub__card-body">
          <p className="writing-hub__kind">
            {item.type === "course" ? <BookOpen aria-hidden="true" size={12} /> : <FileText aria-hidden="true" size={12} />}
            {item.type === "course" ? "Course" : "Article"}
          </p>
          <h2>{item.title}</h2>
          <p className="writing-hub__tagline">{item.tagline}</p>
          <p className="writing-hub__status">{item.statusText}</p>
          {item.ctaTo ? (
            <Link className="course-button course-button--quiet writing-hub__card-cta" to={item.ctaTo}>
              {item.ctaText} <ArrowRight aria-hidden="true" size={16} />
            </Link>
          ) : (
            <span className="course-button course-button--quiet" aria-hidden="true">
              {item.ctaText} <ArrowRight aria-hidden="true" size={16} />
            </span>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="course-shell writing-hub">
      <main>
        <h1 className="sr-only">Writing</h1>

        {loading ? (
          <WritingHubSkeleton />
        ) : (
          <>
            <section className="course-section writing-hub__section writing-hub__wrap" aria-label="All writing">
              <div className="writing-hub__browse">
                <div className="writing-hub__toolbar">
                  <label className="writing-hub__search">
                    <Search aria-hidden="true" size={18} />
                    <span className="sr-only">Search writing</span>
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by title or subject…"
                    />
                  </label>
                  <div className="writing-hub__type-tabs" role="group" aria-label="Content type">
                    {TYPE_TABS.map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        aria-pressed={typeFilter === tab.value}
                        className="writing-hub__type-tab"
                        onClick={() => setTypeFilter(tab.value)}
                      >
                        {tab.label}
                        <span>{counts[tab.value]}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="writing-hub__topics-button"
                    aria-haspopup="dialog"
                    aria-expanded={drawerOpen}
                    onClick={() => setDrawerOpen(true)}
                  >
                    <SlidersHorizontal aria-hidden="true" size={16} />
                    Topics
                    {labelFilters.length > 0 && <strong>{labelFilters.length}</strong>}
                  </button>
                </div>

                {isStacked && (
                  <div
                    className={`writing-hub__drawer-backdrop ${drawerOpen ? "is-open" : ""}`}
                    aria-hidden="true"
                    onClick={() => setDrawerOpen(false)}
                  />
                )}

                <aside
                  ref={filtersRef}
                  className={`writing-hub__filters ${drawerOpen ? "is-open" : ""}`}
                  aria-label="Filter by topic"
                  {...(isStacked && { role: "dialog", "aria-modal": drawerOpen, inert: !drawerOpen })}
                >
                    <div className="writing-hub__topic-heading">
                      <p>Topics</p>
                      {labelFilters.length > 0 && (
                        <button className="writing-hub__clear-filters" type="button" onClick={() => setLabelFilters([])}>
                          Clear ({labelFilters.length})
                        </button>
                      )}
                      {isStacked && (
                        <button
                          ref={drawerCloseRef}
                          type="button"
                          className="writing-hub__drawer-close"
                          aria-label="Close topics"
                          onClick={() => setDrawerOpen(false)}
                        >
                          <X aria-hidden="true" size={18} />
                        </button>
                      )}
                    </div>
                    {WRITING_LABEL_GROUPS.map((group) => {
                      const isOpen = openGroups.includes(group.name);
                      const selectedInGroup = labelFilters.filter((label) => (group.labels as readonly string[]).includes(label)).length;
                      const panelId = `writing-group-${group.name.replace(/\W+/g, "-").toLowerCase()}`;
                      return (
                        <div className="writing-hub__filter-group" key={group.name}>
                          <button
                            type="button"
                            className="writing-hub__group-toggle"
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() =>
                              setOpenGroups((current) =>
                                isOpen ? current.filter((name) => name !== group.name) : [...current, group.name]
                              )
                            }
                          >
                            <ChevronDown aria-hidden="true" size={16} className="writing-hub__group-chevron" />
                            <span>{group.name}</span>
                            {selectedInGroup > 0 && <strong>{selectedInGroup}</strong>}
                          </button>
                          {isOpen && (
                            <div id={panelId} className="writing-hub__group-options">
                              {group.labels.map((label) => (
                                <label key={label} className="writing-hub__filter-checkbox">
                                  <span>
                                    <input
                                      type="checkbox"
                                      checked={labelFilters.includes(label)}
                                      onChange={() => setLabelFilters((current) => toggleWritingLabel(current, label))}
                                    />
                                    {label}
                                  </span>
                                  <span className="writing-hub__filter-count">{labelCounts[label]}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {isStacked && (
                      <div className="writing-hub__drawer-footer">
                        <button type="button" className="writing-hub__drawer-apply" onClick={() => setDrawerOpen(false)}>
                          Show {visibleItems.length} {visibleItems.length === 1 ? "result" : "results"}
                        </button>
                      </div>
                    )}
                  </aside>

                <div className="writing-hub__browse-main">
                  {labelFilters.length > 0 && (
                    <div className="writing-hub__active-topics" aria-label="Active topics">
                      {labelFilters.map((label) => (
                        <button
                          key={label}
                          type="button"
                          className="writing-hub__active-chip"
                          onClick={() => setLabelFilters((current) => toggleWritingLabel(current, label))}
                          aria-label={`Remove ${label}`}
                        >
                          {label}
                          <X aria-hidden="true" size={14} />
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="writing-hub__grid">
                    {visibleItems.length === 0 ? (
                      <p className="writing-hub__empty">
                        {normalizedSearch || typeFilter !== "all" || labelFilters.length > 0 ? "Nothing matches these filters." : "Nothing published yet."}
                      </p>
                    ) : (
                      visibleItems.map(renderCard)
                    )}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default WritingPage;
